import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from './Layout';
import { useBranch } from '../contexts/BranchContext';
import { 
  Session, 
  University, 
  Campus, 
  Course,
  Country,
  UniversityPortalStats 
} from '../types/UniversityPortal';
import { universityPortalApi } from '../services/universityPortalApi';
import api from '../services/api';
import { 
  CalendarIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  BookOpenIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  Squares2X2Icon,
  UserIcon,
  ChevronDownIcon,
  PlusIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import SessionModal from './modals/SessionModal';
import UniversityModal from './modals/UniversityModal';
import CampusModal from './modals/CampusModal';
import PermissionGate from './PermissionGate';
import CourseModal from './modals/CourseModal';
import CountryModal from './modals/CountryModal';
import UniversityTreeView from './UniversityTreeView';
import AddLeadsModal from './modals/AddLeadsModal';
import AssociatedLeadsTab from './AssociatedLeadsTab';
import BatchCreationModal from './modals/BatchCreationModal';

const UniversityPortal: React.FC = () => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [stats, setStats] = useState<UniversityPortalStats | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['associated-leads', 'sessions', 'universities', 'campuses', 'courses', 'countries', 'tree'] as const;
  type TabKey = typeof VALID_TABS[number];
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = (VALID_TABS as readonly string[]).includes(tabParam ?? '') ? (tabParam as TabKey) : 'associated-leads';
  const setActiveTab = useCallback((tab: TabKey) => {
    setSearchParams({ tab });
  }, [setSearchParams]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalType, setModalType] = useState<'session' | 'university' | 'campus' | 'course' | 'country'>('session');

  const [editingItem, setEditingItem] = useState<any>(null);
  const [showAddLeadsModal, setShowAddLeadsModal] = useState(false);
  const [showBatchCreationModal, setShowBatchCreationModal] = useState(false);
  const [associatedLeadsCount, setAssociatedLeadsCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [filteredSessions, setFilteredSessions] = useState<Session[]>([]);
  const [filteredUniversities, setFilteredUniversities] = useState<University[]>([]);
  const [filteredCampuses, setFilteredCampuses] = useState<Campus[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [loadingOperations, setLoadingOperations] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    if (!branchLoading) {
      loadData();
    }
  }, [currentBranch, isViewingAllBranches, branchLoading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAddDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowAddDropdown(false);
      }
    };

    if (showAddDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddDropdown]);

  // Filter data based on search query and status
  useEffect(() => {
    const query = searchQuery.toLowerCase();
    
    const filterBySearchAndStatus = (items: any[]) => {
      return items.filter(item => {
        // Search filter
        const matchesSearch = query === '' || 
          Object.values(item).some(value => 
            value && value.toString().toLowerCase().includes(query)
          );
        
        // Status filter
        const matchesStatus = statusFilter === 'all' || 
          (statusFilter === 'active' && item.isActive) ||
          (statusFilter === 'inactive' && !item.isActive);
        
        return matchesSearch && matchesStatus;
      });
    };
    
    setFilteredSessions(filterBySearchAndStatus(sessions));
    setFilteredUniversities(filterBySearchAndStatus(universities));
    setFilteredCampuses(filterBySearchAndStatus(campuses));
    setFilteredCourses(filterBySearchAndStatus(courses));
    setFilteredCountries(filterBySearchAndStatus(countries));
  }, [searchQuery, statusFilter, sessions, universities, campuses, courses, countries]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsData, sessionsData, universitiesData, campusesData, coursesData, countriesData, associationsCount] = await Promise.all([
        universityPortalApi.getDashboardStats(),
        universityPortalApi.getSessions(),
        universityPortalApi.getUniversities(),
        universityPortalApi.getCampuses(),
        universityPortalApi.getCourses(),
        universityPortalApi.getCountries(),
        fetchAssociatedLeadsCount()
      ]);

      setStats(statsData);
      setSessions(sessionsData);
      setUniversities(universitiesData);
      setCampuses(campusesData);
      setCourses(coursesData);
      setCountries(countriesData);
      setAssociatedLeadsCount(associationsCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociatedLeadsCount = async () => {
    try {
      const response = await api.get('/lead-associations?limit=1');
      return response.data.pagination?.totalRecords || 0;
    } catch (err) {
      console.error('Error fetching associated leads count:', err);
      return 0;
    }
  };

  const setOperationLoading = (operationId: string, isLoading: boolean) => {
    setLoadingOperations(prev => {
      const newSet = new Set(prev);
      if (isLoading) {
        newSet.add(operationId);
      } else {
        newSet.delete(operationId);
      }
      return newSet;
    });
  };

  const isOperationLoading = (operationId: string) => {
    return loadingOperations.has(operationId);
  };

  const handleCreateClick = (type: 'session' | 'university' | 'campus' | 'course' | 'country') => {
    setModalType(type);
    setEditingItem(null);
    setShowCreateModal(true);
  };

  const handleEditClick = (type: 'session' | 'university' | 'campus' | 'course' | 'country', item: any) => {
    setModalType(type);
    setEditingItem(item);
    setShowCreateModal(true);
  };

  const handleStatusToggle = async (type: 'session' | 'university' | 'campus' | 'course' | 'country', item: any) => {
    const operationId = `status-${type}-${item.id}`;
    
    try {
      setOperationLoading(operationId, true);
      const newStatus = !item.isActive;
      const updateData = { isActive: newStatus };

      switch (type) {
        case 'session':
          await universityPortalApi.updateSession(item.id, updateData);
          break;
        case 'university':
          await universityPortalApi.updateUniversity(item.id, updateData);
          break;
        case 'campus':
          await universityPortalApi.updateCampus(item.id, updateData);
          break;
        case 'course':
          await universityPortalApi.updateCourse(item.id, updateData);
          break;
        case 'country':
          await universityPortalApi.updateCountry(item.id, updateData);
          break;
      }
      loadData(); // Reload data after successful status change
    } catch (err: any) {
      setError(err.message || `Failed to update ${type} status`);
    } finally {
      setOperationLoading(operationId, false);
    }
  };

  const handleDeleteClick = async (type: 'session' | 'university' | 'campus' | 'course' | 'country', item: any) => {
    const operationId = `delete-${type}-${item.id}`;
    
    // For universities, check for related data first
    if (type === 'university') {
      try {
        setOperationLoading(operationId, true);
        const relatedCounts = await universityPortalApi.getUniversityRelatedCounts(item.id);
        const totalRelated = relatedCounts.campuses + relatedCounts.courses;
        
        if (totalRelated > 0) {
          const relatedDetails = [];
          if (relatedCounts.campuses > 0) relatedDetails.push(`${relatedCounts.campuses} campus${relatedCounts.campuses > 1 ? 'es' : ''}`);
          if (relatedCounts.courses > 0) relatedDetails.push(`${relatedCounts.courses} course${relatedCounts.courses > 1 ? 's' : ''}`);
          
          const message = `This university has ${relatedDetails.join(' and ')}. Deleting it will also delete all related data. Are you sure you want to continue?`;
          
          if (!window.confirm(message)) {
            setOperationLoading(operationId, false);
            return;
          }
        } else {
          if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
            setOperationLoading(operationId, false);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to check related data:', err);
        setOperationLoading(operationId, false);
        // Fall back to simple confirmation if API fails
        if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
          return;
        }
      }
    } else {
      if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
        return;
      }
    }

    try {
      setOperationLoading(operationId, true);
      switch (type) {
        case 'session':
          await universityPortalApi.deleteSession(item.id);
          break;
        case 'university':
          await universityPortalApi.deleteUniversity(item.id);
          break;
        case 'campus':
          await universityPortalApi.deleteCampus(item.id);
          break;
        case 'course':
          await universityPortalApi.deleteCourse(item.id);
          break;
        case 'country':
          await universityPortalApi.deleteCountry(item.id);
          break;
      }
      loadData(); // Reload data after successful deletion
    } catch (err: any) {
      setError(err.message || `Failed to delete ${type}`);
    } finally {
      setOperationLoading(operationId, false);
    }
  };

  const handleViewClick = (type: 'session' | 'university' | 'campus' | 'course' | 'country', item: any) => {
    // For now, view will open the edit modal in read-only mode
    // You can create a separate view modal later if needed
    setModalType(type);
    setEditingItem(item);
    setShowCreateModal(true);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setEditingItem(null);
    loadData(); // Reload data after successful creation/update
  };

  if (loading) {
    return (
      <Layout title="University Portal">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="University Portal">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">University Portal</h1>
            <p className="text-gray-600">Manage sessions, universities, campuses, and courses</p>
          </div>
          
          {/* Search and Filter Controls */}
          <div className="flex items-center space-x-4">
            {/* Search Bar */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search all data..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-64 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Add Button with Dropdown */}
        <div className="flex justify-end mb-6">
          <div className="relative" ref={dropdownRef}>
          <PermissionGate module="university-portal" permission="create">
            <button
                onClick={() => setShowAddDropdown(!showAddDropdown)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add New
                <ChevronDownIcon className="h-4 w-4 ml-2" />
              </button>
          </PermissionGate>
            
            {showAddDropdown && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 animate-in fade-in-0 zoom-in-95 duration-200">
                  <div className="py-1">
                    <PermissionGate module="university-portal" permission="create">
                      <button
                        onClick={() => {
                          handleCreateClick('session');
                          setShowAddDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <CalendarIcon className="h-5 w-5 mr-3 text-blue-600" />
                        <div className="text-left">
                          <div className="font-medium">Add Session</div>
                          <div className="text-xs text-gray-500">Create a new academic session</div>
                        </div>
                      </button>
                    </PermissionGate>
          
                    <PermissionGate module="university-portal" permission="create">
                      <button
                        onClick={() => {
                          handleCreateClick('university');
                          setShowAddDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
                      >
                        <BuildingOfficeIcon className="h-5 w-5 mr-3 text-green-600" />
                        <div className="text-left">
                          <div className="font-medium">Add University</div>
                          <div className="text-xs text-gray-500">Create a new university</div>
                        </div>
                      </button>
                    </PermissionGate>
          
                    <PermissionGate module="university-portal" permission="create">
                      <button
                        onClick={() => {
                          handleCreateClick('campus');
                          setShowAddDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                      >
                        <MapPinIcon className="h-5 w-5 mr-3 text-purple-600" />
                        <div className="text-left">
                          <div className="font-medium">Add Campus</div>
                          <div className="text-xs text-gray-500">Create a new campus</div>
                        </div>
                      </button>
                    </PermissionGate>
          
                    <PermissionGate module="university-portal" permission="create">
                      <button
                        onClick={() => {
                          handleCreateClick('course');
                          setShowAddDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                      >
                        <BookOpenIcon className="h-5 w-5 mr-3 text-orange-600" />
                        <div className="text-left">
                          <div className="font-medium">Add Course</div>
                          <div className="text-xs text-gray-500">Create a new course</div>
                        </div>
                      </button>
                    </PermissionGate>

                    <PermissionGate module="university-portal" permission="create">
                      <button
                        onClick={() => {
                          handleCreateClick('country');
                          setShowAddDropdown(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                      >
                        <GlobeAltIcon className="h-5 w-5 mr-3 text-teal-600" />
                        <div className="text-left">
                          <div className="font-medium">Add Country</div>
                          <div className="text-xs text-gray-500">Create a new country</div>
                        </div>
                      </button>
                    </PermissionGate>
                    
                    <div className="border-t border-gray-100 my-1"></div>
          
          <button
                      onClick={() => {
                        setShowBatchCreationModal(true);
                        setShowAddDropdown(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    >
                      <DocumentArrowUpIcon className="h-5 w-5 mr-3 text-purple-600" />
                      <div className="text-left">
                        <div className="font-medium">Batch Create</div>
                        <div className="text-xs text-gray-500">Create multiple items from CSV</div>
            </div>
          </button>
                    
                    <button
                      onClick={() => {
                        setShowAddLeadsModal(true);
                        setShowAddDropdown(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                    >
                      <Squares2X2Icon className="h-5 w-5 mr-3 text-indigo-600" />
                      <div className="text-left">
                        <div className="font-medium">Add Leads</div>
                        <div className="text-xs text-gray-500">Associate leads with universities</div>
                      </div>
                    </button>
                  </div>
                </div>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <CalendarIcon className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Sessions</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.sessions.total}</p>
                  <p className="text-xs text-green-600">{stats.sessions.active} active</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <BuildingOfficeIcon className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Universities</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.universities.total}</p>
                  <p className="text-xs text-green-600">{stats.universities.active} active</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <MapPinIcon className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Campuses</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.campuses.total}</p>
                  <p className="text-xs text-green-600">{stats.campuses.active} active</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center">
                <BookOpenIcon className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Courses</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.courses.total}</p>
                  <p className="text-xs text-green-600">{stats.courses.active} active</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'associated-leads', label: 'Associated Leads', count: associatedLeadsCount, icon: UserIcon },
              { key: 'sessions', label: 'Sessions', count: sessions.length, icon: CalendarIcon },
              { key: 'universities', label: 'Universities', count: universities.length, icon: BuildingOfficeIcon },
              { key: 'campuses', label: 'Campuses', count: campuses.length, icon: MapPinIcon },
              { key: 'courses', label: 'Courses', count: courses.length, icon: BookOpenIcon },
              { key: 'countries', label: 'Countries', count: countries.length, icon: GlobeAltIcon },
              { key: 'tree', label: 'Tree View', count: universities.length, icon: Squares2X2Icon }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 inline mr-2" />
                {tab.label} ({tab.count})
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          {activeTab === 'associated-leads' && (
            <AssociatedLeadsTab onRefresh={loadData} />
          )}
          {activeTab === 'sessions' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Sessions</h3>
              {filteredSessions.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No sessions match your search.' : 'No sessions found. Create your first session!'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredSessions.map((session) => (
                        <tr key={session.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {session.code}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {session.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {session.startDate || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {session.endDate || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleStatusToggle('session', session)}
                              disabled={isOperationLoading(`status-session-${session.id}`)}
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${
                                session.isActive 
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              }`}
                              title={`Click to ${session.isActive ? 'deactivate' : 'activate'}`}
                            >
                              {isOperationLoading(`status-session-${session.id}`) ? (
                                <div className="w-2 h-2 bg-current rounded-full animate-pulse" />
                              ) : (
                                session.isActive ? 'Active' : 'Inactive'
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <PermissionGate module="university-portal" permission="view">
                                <button
                                  onClick={() => handleViewClick('session', session)}
                                  className="text-green-600 hover:text-green-900"
                                  title="View"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="edit">
                                <button
                                  onClick={() => handleEditClick('session', session)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="delete">
                                <button
                                  onClick={() => handleDeleteClick('session', session)}
                                  disabled={isOperationLoading(`delete-session-${session.id}`)}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete"
                                >
                                  {isOperationLoading(`delete-session-${session.id}`) ? (
                                    <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <TrashIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'universities' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Universities</h3>
              {filteredUniversities.length === 0 ? (
                <div className="text-center py-8">
                  <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No universities match your search.' : 'No universities found. Create your first university!'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Established</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUniversities.map((university) => (
                        <tr key={university.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {university.name}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {university.description || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {university.website ? (
                              <a href={university.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                {university.website}
                              </a>
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {university.establishedYear || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleStatusToggle('university', university)}
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                                university.isActive 
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              }`}
                              title={`Click to ${university.isActive ? 'deactivate' : 'activate'}`}
                            >
                              {university.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <PermissionGate module="university-portal" permission="view">
                                <button
                                  onClick={() => handleViewClick('university', university)}
                                  className="text-green-600 hover:text-green-900"
                                  title="View"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="edit">
                                <button
                                  onClick={() => handleEditClick('university', university)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="delete">
                                <button
                                  onClick={() => handleDeleteClick('university', university)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'campuses' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Campuses</h3>
              {filteredCampuses.length === 0 ? (
                <div className="text-center py-8">
                  <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No campuses match your search.' : 'No campuses found. Create your first campus!'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">University</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCampuses.map((campus) => (
                        <tr key={campus.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {campus.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {campus.universityName || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {campus.city}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {campus.country}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              campus.isMainCampus 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {campus.isMainCampus ? 'Main Campus' : 'Branch Campus'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleStatusToggle('campus', campus)}
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                                campus.isActive 
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              }`}
                              title={`Click to ${campus.isActive ? 'deactivate' : 'activate'}`}
                            >
                              {campus.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <PermissionGate module="university-portal" permission="view">
                                <button
                                  onClick={() => handleViewClick('campus', campus)}
                                  className="text-green-600 hover:text-green-900"
                                  title="View"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="edit">
                                <button
                                  onClick={() => handleEditClick('campus', campus)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="delete">
                                <button
                                  onClick={() => handleDeleteClick('campus', campus)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'courses' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Courses</h3>
              {filteredCourses.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpenIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No courses match your search.' : 'No courses found. Create your first course!'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">University</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campus</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCourses.map((course) => (
                        <tr key={course.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {course.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {course.code || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {course.universityName || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {course.campusName || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {course.credits || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {course.durationMonths ? `${course.durationMonths} months` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleStatusToggle('course', course)}
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                                course.isActive 
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              }`}
                              title={`Click to ${course.isActive ? 'deactivate' : 'activate'}`}
                            >
                              {course.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <PermissionGate module="university-portal" permission="view">
                                <button
                                  onClick={() => handleViewClick('course', course)}
                                  className="text-green-600 hover:text-green-900"
                                  title="View"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="edit">
                                <button
                                  onClick={() => handleEditClick('course', course)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="delete">
                                <button
                                  onClick={() => handleDeleteClick('course', course)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {activeTab === 'countries' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Countries</h3>
              {filteredCountries.length === 0 ? (
                <div className="text-center py-8">
                  <GlobeAltIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No countries match your search.' : 'No countries found. Create your first country!'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCountries.map((country) => (
                        <tr key={country.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {country.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {country.code || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleStatusToggle('country', country)}
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                                country.isActive 
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              }`}
                              title={`Click to ${country.isActive ? 'deactivate' : 'activate'}`}
                            >
                              {country.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <PermissionGate module="university-portal" permission="view">
                                <button
                                  onClick={() => handleViewClick('country', country)}
                                  className="text-green-600 hover:text-green-900"
                                  title="View"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="edit">
                                <button
                                  onClick={() => handleEditClick('country', country)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate module="university-portal" permission="delete">
                                <button
                                  onClick={() => handleDeleteClick('country', country)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tree View Tab */}
          {activeTab === 'tree' && (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">University Tree View</h3>
              <p className="text-sm text-gray-600 mb-6">
                Hierarchical view of universities, their campuses, and courses
              </p>
              
              {filteredUniversities.length === 0 ? (
                <div className="text-center py-8">
                  <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {searchQuery ? 'No universities match your search.' : 'No universities found. Create your first university!'}
                  </p>
                </div>
              ) : (
                <UniversityTreeView
                  universities={filteredUniversities}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  onView={handleViewClick}
                  loadingOperations={loadingOperations}
                  isOperationLoading={isOperationLoading}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <SessionModal
        isOpen={showCreateModal && modalType === 'session'}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        session={editingItem}
      />
      
      <UniversityModal
        isOpen={showCreateModal && modalType === 'university'}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        university={editingItem}
      />
      
      <CampusModal
        isOpen={showCreateModal && modalType === 'campus'}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        campus={editingItem}
      />
      
      <CourseModal
        isOpen={showCreateModal && modalType === 'course'}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        course={editingItem}
      />
      
      <CountryModal
        isOpen={showCreateModal && modalType === 'country'}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        country={editingItem}
      />
      
      <AddLeadsModal
        isOpen={showAddLeadsModal}
        onClose={() => setShowAddLeadsModal(false)}
        onSuccess={handleCreateSuccess}
      />
      
      <BatchCreationModal
        isOpen={showBatchCreationModal}
        onClose={() => setShowBatchCreationModal(false)}
        onSuccess={handleCreateSuccess}
        universities={universities}
        campuses={campuses}
      />
    </Layout>
  );
};

export default UniversityPortal;
